import React, { useState, useEffect, useCallback } from "react";
import Map, { Marker, Source, Layer } from "@vis.gl/react-mapbox";
import * as turf from "@turf/turf";
import axios from "axios";

const MAPBOX_TOKEN = "pk.eyJ1IjoiYWxlcnRsaW5lIiwiYSI6ImNtODMweDM2ZzAwdGQycXI0enVwZndtOHgifQ.yJ8KJp8yHrr3midLMoDbHg";

const MapboxLocationSelector = ({ 
  latitude, 
  longitude, 
  geofenceRadius, 
  zipCode,
  onLocationChange,
  onGeofenceChange,
  onZipCodeChange 
}) => {
  const [clicks, setClicks] = useState([]);
  const [radius, setRadius] = useState(null);
  const [center, setCenter] = useState(null);
  const [tempRadius, setTempRadius] = useState(null);
  const [isFinalized, setIsFinalized] = useState(false);

  const [viewState, setViewState] = useState({
    latitude: 53.276749,
    longitude: -6.21918,
    zoom: 16,
  });

  // Initialize map with existing coordinates
  useEffect(() => {
    if (latitude && longitude) {
      setViewState(prev => ({
        ...prev,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
      }));
      setCenter([parseFloat(longitude), parseFloat(latitude)]);
      setClicks([[parseFloat(longitude), parseFloat(latitude)]]);
      if (geofenceRadius) {
        setRadius(parseFloat(geofenceRadius));
      }
    }
  }, [latitude, longitude, geofenceRadius]);

  // Handle map click
  const handleMapClick = (e) => {
    const { lng, lat } = e.lngLat;

    if (clicks.length === 0) {
      // First click: Set center with initial 5m radius
      setClicks([[lng, lat]]);
      setCenter([lng, lat]);
      setRadius(5); // Set initial radius to 5 meters
      setTempRadius(null);
      setIsFinalized(false); // Allow second click to update radius
      onLocationChange(lat, lng);
      onGeofenceChange(5);
      reverseGeocode(lng, lat);
    } else if (clicks.length === 1 && !isFinalized) {
      // Second click: Update radius and finalize
      const firstPoint = clicks[0];
      const secondPoint = [lng, lat];
      const from = turf.point(firstPoint);
      const to = turf.point(secondPoint);
      const dist = turf.distance(from, to, { units: 'meters' });

      setRadius(dist);
      setTempRadius(null);
      setIsFinalized(true); // Mark as finalized - third click will reset
      onGeofenceChange(dist);
    } else {
      // Third click: Reset everything and repeat
      setClicks([]);
      setCenter(null);
      setRadius(null);
      setTempRadius(null);
      setIsFinalized(false);
      onLocationChange(null, null);
      onGeofenceChange(null);
      onZipCodeChange('');
    }
  };

  // Handle mouse move for radius preview
  const handleMouseMove = useCallback((e) => {
    // Show dynamic preview if we have center and not finalized
    if (clicks.length === 1 && center && !isFinalized) {
      const firstPoint = clicks[0];
      const cursorPoint = [e.lngLat.lng, e.lngLat.lat];
      const from = turf.point(firstPoint);
      const to = turf.point(cursorPoint);
      const dist = turf.distance(from, to, { units: 'meters' });
      setTempRadius(dist);
    }
  }, [clicks, center, isFinalized]);

  // Handle map movement
  const handleMapMove = (evt) => {
    setViewState(evt.viewState);
  };

  // Forward geocoding: Postal code to coordinates
  const handlePostalCodeSearch = async () => {
    if (!zipCode.trim()) {
      alert('Please enter a valid postal code.');
      return;
    }
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(zipCode)}.json`,
        {
          params: {
            access_token: MAPBOX_TOKEN,
            types: 'postcode',
            limit: 1,
          },
        }
      );
      if (response.data.features.length === 0) {
        alert('No results found for the postal code.');
        return;
      }
      const [lng, lat] = response.data.features[0].center;
      setViewState(prev => ({
        ...prev,
        longitude: lng,
        latitude: lat,
        zoom: 18,
      }));
      setCenter([lng, lat]);
      setClicks([[lng, lat]]);
      setRadius(null);
      setTempRadius(null);
      onLocationChange(lat, lng);
      onGeofenceChange(null);
    } catch (error) {
      console.error('Error geocoding postal code:', error);
      alert('Failed to geocode postal code. Please try again.');
    }
  };

  // Reverse geocoding
  const reverseGeocode = async (lng, lat) => {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`,
        {
          params: {
            access_token: MAPBOX_TOKEN,
            types: 'postcode',
            limit: 1,
          },
        }
      );
      const postalCode = response.data.features[0]?.place_name || '';
      onZipCodeChange(postalCode);
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      onZipCodeChange('');
    }
  };

  // Create circle for geofence
  const dynamicRadius = radius || tempRadius;
  const circle = center && dynamicRadius && dynamicRadius > 0
    ? turf.circle(center, dynamicRadius / 1000, { steps: 64, units: 'kilometers' })
    : null;
  const geojson = circle
    ? {
        type: 'FeatureCollection',
        features: [circle],
      }
    : null;

  // Map layer styles
  const layerStyleFill = {
    id: 'geofence-fill',
    type: 'fill',
    paint: {
      'fill-color': '#007AFF',
      'fill-opacity': 0.1,
    },
  };
  const layerStyleLine = {
    id: 'geofence-border',
    type: 'line',
    paint: {
      'line-color': '#007AFF',
      'line-width': 2,
    },
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Postal Code Search
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={zipCode || ''}
            onChange={(e) => onZipCodeChange(e.target.value)}
            placeholder="Enter postal code"
            className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={handlePostalCodeSearch}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Enter a postal code to find the location on the map
        </p>
      </div>

      <div className="h-[500px] w-full border border-gray-300 rounded-lg overflow-hidden">
        <Map
          {...viewState}
          onMove={handleMapMove}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          mapboxAccessToken={MAPBOX_TOKEN}
          className="w-full h-full"
          mapStyle="mapbox://styles/mapbox/streets-v11"
        >
          {/* Circle with border and fill */}
          {geojson && (
            <Source id="geofence-source" type="geojson" data={geojson}>
              <Layer {...layerStyleFill} />
              <Layer {...layerStyleLine} />
            </Source>
          )}

          {/* Center Point Marker */}
          {center && (
            <Marker longitude={center[0]} latitude={center[1]}>
              <div className="w-4 h-4 rounded-full bg-[blue]" />
            </Marker>
          )}
        </Map>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p><strong>Instructions:</strong></p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Click on the map to set the location (blue center point)</li>
          <li>Move your mouse to see the dynamic blue circle preview</li>
          <li>Click again to set the geofence radius (circle becomes fixed)</li>
          <li>Click a third time to reset and start over</li>
          <li>Use postal code search to find specific locations</li>
        </ul>
      </div>
    </div>
  );
};

export default MapboxLocationSelector;
